import React from 'react';
import styled from '@emotion/styled';
import { theme } from '../../styles/theme';
import { useNavigate } from 'react-router-dom';
import { WebsiteMarketingCtaButtonFullWidth } from '../common/websiteSection';
import {
  getContactSalesNavigationState,
  isContactSalesPackage,
} from '../../utils/contactSalesPackage';
const Card = styled.div`
  padding: ${theme.spacing.xl};
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  position: relative;
  background: var(--login-form-bg);
  border: 1.5px solid
    ${(props) =>
      props.$selected || props.$popular
        ? 'var(--login-primary)'
        : 'var(--login-form-border)'};
  box-shadow: ${(props) =>
    props.$selected || props.$popular
      ? '0 20px 40px rgba(72, 138, 236, 0.12)'
      : 'var(--login-form-shadow)'};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  cursor: ${(props) => (props.$selectable ? 'pointer' : 'default')};
  text-align: left;

  &:hover {
    border-color: rgba(72, 138, 236, 0.35);
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1);
  }
`;

const PopularBadge = styled.span`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 14px;
  border-radius: 999px;
  background: var(--login-primary);
  color: #fff;
  font-size: ${theme.typography.fontSize.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
`;

const PackageName = styled.h3`
  margin: 0;
  font-size: ${theme.typography.fontSize.xl};
  font-weight: 700;
  color: var(--login-form-text);
  letter-spacing: -0.02em;
`;

const Price = styled.div`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: 800;
  color: var(--login-primary);
  letter-spacing: -0.03em;
  line-height: 1.1;

  span {
    font-size: ${theme.typography.fontSize.base};
    font-weight: 500;
    color: var(--login-form-muted);
  }
`;
const Description = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.55;
  color: var(--login-form-muted);
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${theme.spacing.sm};
  flex: 1;
`;

const Feature = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  padding: 10px 0;
  border-bottom: 1px solid var(--login-form-border);
  font-size: ${theme.typography.fontSize.sm};
  color: var(--login-form-muted);
  line-height: 1.45;

  &:last-child {
    border-bottom: none;
  }

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--login-primary);
  }
`;

const PlanButton = styled(WebsiteMarketingCtaButtonFullWidth)`
  margin-top: auto;
  border-radius: 999px;
  padding: 14px 22px;
  box-shadow: none;
  background: var(--login-primary);
  text-decoration: none;

  &:hover:not(:disabled) {
    transform: none;
    box-shadow: none;
    background: var(--login-primary-dark);
  }
`;

const PricingCard = ({
  package: pkg,
  billingCycle = 'monthly',
  selectable = false,
  isSelected = false,
  onSelect,
  actionLabel,
}) => {
  const navigate = useNavigate();
  const contactOnly = isContactSalesPackage(pkg);

  const getPrice = () => (billingCycle === 'monthly' ? pkg.monthlyPrice : pkg.yearlyPrice);

  const handlePrimaryAction = () => {
    if (contactOnly) {
      navigate('/contact', { state: getContactSalesNavigationState(pkg) });
      return;
    }

    if (selectable) {
      onSelect?.(pkg.id);
      return;
    }

    navigate('/main/account-setup', {
      state: { selectedPackageId: pkg.id },
    });
  };
  const handleCardClick = () => {
    if (contactOnly) {
      handlePrimaryAction();
      return;
    }

    if (selectable) {
      onSelect?.(pkg.id);
    }
  };

  const buttonLabel = actionLabel || pkg.buttonText || (contactOnly ? 'Contact Us' : 'Get Started');
  return (
    <Card
      $popular={pkg.isPopular}
      $selectable={selectable && !contactOnly}
      $selected={isSelected}
      onClick={selectable ? handleCardClick : undefined}
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : undefined}
      onKeyDown={
        selectable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect?.(pkg.id);
              }
            }
          : undefined
      }
      aria-pressed={selectable ? isSelected : undefined}
    >
      {pkg.isPopular ? <PopularBadge>Most popular</PopularBadge> : null}

      <PackageName>{pkg.name}</PackageName>

      <Price>
        {contactOnly ? '$???' : `$${getPrice()}`}
        <span>/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
      </Price>
      <Description>{pkg.description}</Description>

      <FeaturesList>
        {pkg.features.map((feature) => (
          <Feature key={feature.id}>
            {feature.included ? (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
                <path
                  d="M16.7 5.3c.4.4.4 1 0 1.4l-8 8c-.4.4-1 .4-1.4 0l-4-4c-.4-.4-.4-1 0-1.4.4-.4 1-.4 1.4 0L8 12.6l7.3-7.3c.4-.4 1-.4 1.4 0z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
                <path
                  d="M15 5L5 15M5 5l10 10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {feature.name}
          </Feature>
        ))}
      </FeaturesList>

      <PlanButton
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handlePrimaryAction();
        }}
      >
        {selectable && isSelected ? 'Selected' : buttonLabel}
      </PlanButton>
    </Card>
  );
};

export default PricingCard;
